import { useFormContext } from 'react-hook-form';

import MultiSelect from '@lib/core/src/components/MultiSelect';
import MultiToggle, { MultiToggleOption } from '@lib/core/src/components/MultiToggle';
import palette from '@lib/core/src/theme/palette';
import { MenuItem, Select, SelectChangeEvent, Stack, styled } from '@mui/material';
import { RiCalendar2Line, RiLink } from '@remixicon/react';

import { WorkflowMeetingsContextDtoScope } from '@/api/generated';
import { Card, HelperText, Row } from '@/components/AiWorkflow/AiWorkflowStyles';
import { DEFAULT_TIME_RANGE, TIME_RANGE_OPTIONS } from '@/components/AiWorkflow/formSchema';

const Container = styled(Card)({
  gap: 20
});

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 16
});

const HeightBox = styled('div')({
  height: 44
});

const IconBox = styled(HeightBox)({
  width: 44,
  borderRadius: 24,
  background: palette.backgrounds[300],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const Title = styled('div')({
  fontFamily: 'Reddit Sans',
  fontWeight: 500,
  fontSize: 16,
  color: palette.black,
  lineHeight: '21px'
});

const SectionTitle = styled('span')({
  fontFamily: 'Reddit Sans',
  fontWeight: 500,
  fontSize: 11,
  color: palette.grey[500],
  lineHeight: '14px',
  marginBottom: 6
});

const Description = styled('div')({
  fontFamily: 'Reddit Sans',
  fontWeight: 500,
  fontSize: 13,
  color: palette.grey[500],
  lineHeight: '16px',
  marginTop: 2
});

const DropdownWrapper = styled('div')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center'
});

const IconLeft = styled(RiCalendar2Line)({
  position: 'absolute',
  left: 12,
  pointerEvents: 'none',
  zIndex: 1
});

const StyledDropdown = styled(Select)(() => ({
  padding: '12px 10px',
  alignItems: 'center',
  gap: '14px',
  width: '100%',
  height: '42px',
  borderRadius: '8px',
  border: `1px solid ${palette.stroke.idle}`,
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  }
}));

const SCOPE_OPTIONS: MultiToggleOption[] = [
  {
    label: 'All meetings',
    value: WorkflowMeetingsContextDtoScope.all,
    icon: <HeightBox />
  },
  {
    label: 'Internal meetings',
    value: WorkflowMeetingsContextDtoScope.internal,
    icon: <HeightBox />
  },
  {
    label: 'External meetings',
    value: WorkflowMeetingsContextDtoScope.external,
    icon: <HeightBox />
  }
];

const BasedOn = () => {
  const {
    register,
    setValue,
    watch,
    formState: { errors }
  } = useFormContext();

  const titleValid = !errors.title && !!watch('title');
  const promptValid = !errors.prompt && !!watch('prompt');
  const focusEmpty = watch('timeRange') === DEFAULT_TIME_RANGE;

  return (
    <Container>
      <Header>
        <IconBox>
          <RiLink size={20} color={palette.grey[400]} />
        </IconBox>
        <div>
          <Title>Based on</Title>
          <Description>Which meetings should this agent rely on?</Description>
        </div>
      </Header>
      <MultiToggle
        minWidth="180px"
        options={SCOPE_OPTIONS}
        value={watch('scope') || ''}
        onChange={val => setValue('scope', val)}
      />
      <Stack>
        <Row>
          <SectionTitle>Meetings focus</SectionTitle>
          {titleValid && promptValid && focusEmpty && <HelperText>Focus is required</HelperText>}
        </Row>

        <DropdownWrapper>
          <IconLeft size={18} />
          <StyledDropdown
            {...register('timeRange')}
            value={watch('timeRange')}
            onChange={(event: SelectChangeEvent<unknown>) =>
              setValue('timeRange', event.target.value as string, {
                shouldValidate: true,
                shouldTouch: true
              })
            }
            style={{ paddingLeft: 20, fontSize: 13 }}
          >
            <MenuItem value={DEFAULT_TIME_RANGE} disabled sx={{ fontSize: 13 }}>
              Select time range
            </MenuItem>
            {TIME_RANGE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                {option.label}
              </MenuItem>
            ))}
          </StyledDropdown>
        </DropdownWrapper>
      </Stack>
      <Stack>
        <SectionTitle></SectionTitle>
        <MultiSelect
          startLabel="Automatically apply if the meeting title contains"
          endLabel="Type a keyword, then hit Enter"
          options={[]}
          value={watch('keyWords') ?? []}
          onChange={(val: string[]) => setValue('keyWords', val)}
          placeholder="Type a keyword, then hit Enter"
        />
      </Stack>
    </Container>
  );
};

export default BasedOn;
